// Le thème : un accent de couleur et un vocabulaire. Les deux sont interchangeables, pour qu'un
// futur cerveau (celui de Camille) garde le même moteur avec d'autres mots et d'autres couleurs.

/** Les thèmes : Nocturne (par défaut, celui de SuperAlfred) ou la DA « Système » de la console Brain. */
export const THEMES = [
  { id: 'nocturne', nom: 'Nocturne' },
  { id: 'systeme', nom: 'Solo Leveling' },
] as const;

export type Theme = (typeof THEMES)[number]['id'];

/** Les quatre accents de Nocturne (réglages de la console SuperAlfred), par teinte OKLCH. */
export const ACCENTS = [
  { nom: 'Blurple', valeur: '292' },
  { nom: 'Cyan', valeur: '225' },
  { nom: 'Vert', valeur: '158' },
  { nom: 'Ambre', valeur: '72' },
] as const;

/** Les trois accents de la console Brain (thème « Système »). */
export const ACCENTS_SYSTEME = [
  { nom: 'Violet', valeur: '#a78bfa' },
  { nom: 'Bleu Système', valeur: '#5ec8ff' },
  { nom: 'Or', valeur: '#f0b95a' },
] as const;

const CLE_THEME = 'alfred.theme';
const CLE_ACCENT: Record<Theme, string> = { nocturne: 'alfred.accent', systeme: 'alfred.accent.systeme' };

/** La couleur de la barre d'état (iPhone, Android) : le fond de chaque thème. */
const FOND: Record<Theme, string> = { nocturne: '#161826', systeme: '#06071a' };

/** Ce que appliquerApparence pose en ligne, et retire en changeant de thème. */
const EN_LIGNE = ['--color-accent', '--color-accent-300', '--color-accent-400', '--color-accent-500', '--color-accent-700', '--color-accent-900', '--sys-accent'];

function lire(cle: string): string | null {
  try {
    return localStorage.getItem(cle);
  } catch {
    return null;
  }
}

function ecrire(cle: string, valeur: string): void {
  try {
    localStorage.setItem(cle, valeur);
  } catch {
    /* préférence non retenue, sans conséquence */
  }
}

export function lireTheme(): Theme {
  const v = lire(CLE_THEME);
  return THEMES.some((t) => t.id === v) ? (v as Theme) : 'nocturne';
}

export function accentsDu(theme: Theme): readonly { nom: string; valeur: string }[] {
  return theme === 'systeme' ? ACCENTS_SYSTEME : ACCENTS;
}

/** L'accent retenu pour ce thème (chaque thème garde le sien). */
export function lireAccent(theme: Theme = lireTheme()): string {
  const choix = accentsDu(theme);
  const v = lire(CLE_ACCENT[theme]);
  return v && choix.some((a) => a.valeur === v) ? v : choix[0]!.valeur;
}

/** Les variables sont écrites sur l'élément racine, jamais dans une feuille de style (Nocturne). */
export function appliquerApparence(theme: Theme, accent: string = lireAccent(theme)): void {
  const racine = document.documentElement;
  racine.dataset.theme = theme;
  const r = racine.style;
  for (const p of EN_LIGNE) r.removeProperty(p);
  if (theme === 'systeme') {
    // L'accent du Système est une couleur ; les nuances Nocturne qu'en lisent les écrans d'Alfred en dérivent.
    r.setProperty('--sys-accent', accent);
    r.setProperty('--color-accent', accent);
    r.setProperty('--color-accent-300', `color-mix(in srgb, ${accent} 70%, white)`);
    r.setProperty('--color-accent-400', `color-mix(in srgb, ${accent} 85%, white)`);
    r.setProperty('--color-accent-500', accent);
    r.setProperty('--color-accent-700', `color-mix(in srgb, ${accent} 55%, #06071a)`);
    r.setProperty('--color-accent-900', `color-mix(in srgb, ${accent} 22%, #06071a)`);
  } else {
    r.setProperty('--color-accent', `oklch(0.68 0.12 ${accent})`);
    r.setProperty('--color-accent-300', `oklch(0.79 0.10 ${accent})`);
    r.setProperty('--color-accent-400', `oklch(0.72 0.12 ${accent})`);
    r.setProperty('--color-accent-500', `oklch(0.68 0.12 ${accent})`);
    r.setProperty('--color-accent-700', `oklch(0.45 0.09 ${accent})`);
    r.setProperty('--color-accent-900', `oklch(0.27 0.05 ${accent})`);
  }
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', FOND[theme]);
  ecrire(CLE_THEME, theme);
  ecrire(CLE_ACCENT[theme], accent);
}

/** Vocabulaire « Système » (Solo Leveling). */
export const VOCAB = {
  systeme: 'SYSTÈME',
  joueur: 'Joueur',
  notification: 'NOTIFICATION',
  queteDuJour: 'QUÊTE DU JOUR',
  queteQuotidienne: 'Quête quotidienne',
  infoQuete: 'INFO QUÊTE',
  objectif: 'OBJECTIF',
  alertes: 'ALERTES',
  enRetard: 'EN RETARD',
  portails: 'PORTAILS',
  portailOuvert: 'PORTAIL OUVERT',
  statut: 'STATUT',
  debuffs: 'DEBUFFS ACTIFS',
  progression: 'PROGRESSION',
  accepter: 'ACCEPTER',
  valider: 'VALIDER',
  niveau: 'NIV.',
  aDebloquer: '[ À débloquer ]',
  nav: { alfred: 'Alfred', brief: 'Brief', quetes: 'Quêtes', portails: 'Portails', cerveau: 'Cerveau', statut: 'Statut' },
} as const;
